import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MeetingTile from './MeetingTile';
import { getOptimalTileSize } from '../../utils/tileGrid';
import { useLanguage } from '../../i18n/LanguageContext';

// How far a voice may be pushed towards one ear. Full-scale panning is
// unpleasant on headphones and leaves a mono listener hearing nothing at all
// from one side of the grid.
const PAN_RANGE = 0.7;

// Everything on stage is reduced to one flat list of "items" before layout -
// a participant's camera and that same participant's shared screen are two
// independent tiles with the same identity, so they need distinct keys and
// must be able to be pinned separately.
function buildItems(participants, videoTracks, screenShareTracks, screenShareAudioTracks) {
    const items = [];
    participants.forEach((participant) => {
        // Bots (the music/transcript bots reuse these identities in group
        // rooms) have nothing to render and should never take a grid slot.
        if (participant.identity.startsWith('bot:')) return;
        const screenTrack = screenShareTracks[participant.identity];
        if (screenTrack) {
            items.push({
                key: `screen:${participant.identity}`,
                participant,
                track: screenTrack,
                // Only present when the sharer included system/tab audio.
                audioTrack: screenShareAudioTracks[participant.identity],
                screenShare: true
            });
        }
        items.push({ key: `cam:${participant.identity}`, participant, track: videoTracks[participant.identity], screenShare: false });
    });
    return items;
}

export default function MeetingStage({
    participants, videoTracks, screenShareTracks, screenShareAudioTracks,
    pinnedKey, onTogglePin, raisedHands, audioLevels, onVolumeChange, canRemove, onRemove,
    annotations, localIdentity, onStroke, onPointer, onClearAnnotations, onPanChange, togetherMode
}) {
    const { t } = useLanguage();
    const [gridSize, setGridSize] = useState(null);
    const observerRef = useRef(null);

    // A callback ref, not useRef + an effect. The grid element does not exist
    // on the first render (an empty stage returns early, and the spotlight
    // layout has no grid at all), so an effect keyed on anything else would
    // run while there was nothing to observe and never run again once there
    // was - leaving gridSize null, which collapses every tile to zero height,
    // because .MeetTile derives its height from a width it only gets from the
    // measured column size. This fires exactly when the node attaches or
    // detaches, which is the thing that actually matters.
    const gridRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!node) {
            setGridSize(null);
            return;
        }
        // Re-measured on every resize (window resize, side panel opening,
        // phone rotation) so the column count stays optimal instead of being
        // frozen at whatever size the stage happened to mount at.
        const observer = new ResizeObserver(([entry]) => {
            setGridSize({ width: entry.contentRect.width, height: entry.contentRect.height });
        });
        observer.observe(node);
        observerRef.current = observer;
    }, []);

    const items = useMemo(
        () => buildItems(participants, videoTracks, screenShareTracks, screenShareAudioTracks),
        [participants, videoTracks, screenShareTracks, screenShareAudioTracks]
    );

    // A shared screen is the reason everyone is looking at the stage, so it
    // takes the spotlight by itself - but never over an explicit pin, which is
    // someone deliberately saying "show me this one instead".
    const autoFeatured = items.find((item) => item.screenShare);
    const featured = items.find((item) => item.key === pinnedKey) || autoFeatured || null;
    const others = featured ? items.filter((item) => item.key !== featured.key) : items;

    const spotlightMode = !!featured;

    // `key` is deliberately not in here - React treats it as a directive
    // rather than a prop, so it has to be written on the element itself to be
    // read reliably rather than arriving through a spread.
    // Declared ahead of the hooks below rather than just before the grid it
    // sizes: the spatial-audio effect needs the column count too, and every
    // hook has to run before this component's early returns.
    const optimal = gridSize ? getOptimalTileSize(items.length, gridSize.width, gridSize.height) : null;

    // Spatial audio: a voice should come from where its tile is. Derived from
    // the grid position rather than measured off the DOM - the column count is
    // already known here, and a layout read per participant per re-render
    // would be a lot of work for scenery.
    //
    // Scaled well short of the extremes: hard-panning a voice to one ear is
    // fatiguing over a long call and actively unhelpful for anyone listening
    // on one side.
    const columns = optimal ? optimal.cols : 1;
    const panKey = `${spotlightMode}:${columns}:${items.map((item) => item.key).join(',')}`;
    useEffect(() => {
        if (!onPanChange) return;
        items.forEach((item, index) => {
            if (item.participant.isLocal) return; // you are not somewhere else in the room
            let pan = 0;
            if (spotlightMode) {
                // The featured speaker stays centred; the filmstrip spreads
                // along the bottom, which is still left-to-right.
                const others = items.filter((other) => other.key !== featured.key);
                const position = others.indexOf(item);
                if (position >= 0 && others.length > 1) pan = ((position + 0.5) / others.length) * 2 - 1;
            } else if (columns > 1) {
                const column = index % columns;
                pan = ((column + 0.5) / columns) * 2 - 1;
            }
            onPanChange(item.participant.identity, pan * PAN_RANGE);
        });
        // panKey collapses "did anything about the layout change" into one
        // string, so this does not re-run on every volume or speaking update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panKey, onPanChange]);

    const tileProps = (item) => ({
        participant: item.participant,
        track: item.track,
        audioTrack: item.audioTrack,
        screenShare: item.screenShare,
        isPinned: pinnedKey === item.key,
        onTogglePin: () => onTogglePin(item.key),
        handRaised: !!raisedHands[item.participant.identity] && !item.screenShare,
        // 0..1, so the speaking ring can respond to how loudly someone is
        // actually talking instead of flipping between two fixed states. A
        // screen share has no voice of its own.
        audioLevel: item.screenShare ? 0 : ((audioLevels && audioLevels[item.participant.identity]) || 0),
        volume: item.participant.volume,
        onVolumeChange,
        canRemove: canRemove && !item.participant.isLocal,
        onRemove,
        // Only the screen-share tile wires these up; a camera tile has nothing
        // to annotate, so it is handed nothing to annotate with.
        annotations: item.screenShare ? annotations : null,
        localIdentity,
        onStroke,
        onPointer,
        onClearAnnotations
    });

    if (items.length === 0) {
        return <div className="MeetStage MeetStageEmpty">{t('meeting.waitingForOthers')}</div>;
    }

    if (spotlightMode) {
        return (
            <div className={`MeetStage MeetStageSpotlight ${togetherMode ? 'MeetStageTogether' : ''}`}>
                <div className="MeetSpotlightMain">
                    <MeetingTile key={featured.key} {...tileProps(featured)} />
                </div>
                {others.length > 0 && (
                    <div className="MeetFilmstrip">
                        {others.map((item) => <MeetingTile key={item.key} {...tileProps(item)} />)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`MeetStage ${togetherMode ? 'MeetStageTogether' : ''}`}>
            <div
                className="MeetGrid"
                ref={gridRef}
                style={optimal ? { gridTemplateColumns: `repeat(${optimal.cols}, ${optimal.tileWidth}px)` } : undefined}
            >
                {items.map((item) => <MeetingTile key={item.key} {...tileProps(item)} />)}
            </div>
        </div>
    );
}
